/**
 * Eventos para o Google Tag Manager de cada prova (injetado pelo
 * `vite.config.ts` só no build que tem `VITE_GTM_ID_<PROVA>`). Mesmo desenho do
 * `lib/analytics.ts` do `enamed-feedback-lp`, e o plano completo — quando cada
 * evento sai e como configurar o GTM — está no documento "Plano de tracking
 * (GTM)".
 *
 * **A lista é fechada e tipada**: um nome de evento digitado à mão em cada tela
 * vira, na primeira divergência, duas linhas no relatório para a mesma coisa.
 *
 * **Os eventos de lead usam os nomes recomendados do GA4** (`sign_up`, `login`,
 * `generate_lead`, `share`): com eles o GA4 monta os relatórios sozinho e o
 * marketing marca a conversão sem tradução. Os do produto (ensaio, gabarito,
 * painel) são nossos, em português e `snake_case`.
 *
 * **Nada pessoal entra.** Nome, e-mail e telefone ficam fora de todos os
 * eventos: a política do Google proíbe dado pessoal no `dataLayer`, e sob a LGPD
 * mandá-lo a um terceiro precisa de base própria. Por isso nenhum evento aceita
 * campo livre — só valores de listas fechadas e contagens.
 */

/** De onde a pessoa chegou ao login/cadastro. */
export type AuthOrigem = 'ensaio' | 'gabarito' | 'login';

export type AnalyticsEvent =
  // ── Landing ────────────────────────────────────────────────────────────
  /**
   * Clique num botão de entrada do funil (home ou painel). Na home, o funil
   * segue o envio do gabarito: aberto, `enviar_gabarito` no hero e no
   * fechamento; senão, `comecar_ensaio`.
   */
  | {
      event: 'cta_clicado';
      cta: 'comecar_ensaio' | 'enviar_gabarito';
      local: 'hero' | 'fechamento' | 'painel';
    }
  // ── Ensaio ─────────────────────────────────────────────────────────────
  /** Clicou em "Baixar o ensaio" (só existe na edição que tem o arquivo). */
  | { event: 'ensaio_download_clicado' }
  /**
   * Clicou em "Previsões Aristo" (só existe na edição que tem o link). É a
   * saída para a página de download do PDF, em aba nova — interesse em
   * material da Aristo, sinal de lead quente.
   */
  | { event: 'previsoes_aristo_clicado' }
  /**
   * Confirmou a folha de "Fornecer respostas": as respostas de quem resolveu
   * no papel entraram no ensaio de uma vez.
   */
  | { event: 'ensaio_respostas_fornecidas'; respondidas: number; total: number }
  /** Primeira resposta do ensaio: a pessoa começou de fato. */
  | { event: 'ensaio_iniciado'; total: number }
  /** Marcos de avanço — 25, 50, 75 e 100% das questões respondidas. */
  | { event: 'ensaio_progresso'; percentual: 25 | 50 | 75 | 100 }
  /** Confirmou "Finalizar" no diálogo (antes do cadastro e do envio). */
  | { event: 'ensaio_finalizado'; respondidas: number; total: number }
  /** **Conversão**: o servidor recebeu o ensaio. */
  | { event: 'ensaio_enviado'; respondidas: number }
  // ── Gabarito ───────────────────────────────────────────────────────────
  /** Clicou em "Baixar o gabarito" (só existe na edição que tem o arquivo). */
  | { event: 'gabarito_download_clicado' }
  /** Primeira marcação na folha. */
  | { event: 'gabarito_iniciado'; total: number }
  /** Confirmou "Enviar" no diálogo (antes do login e do envio). */
  | { event: 'gabarito_finalizado'; respondidas: number; total: number }
  /**
   * **Conversão principal**: o servidor recebeu o gabarito. `tipo_envio`
   * separa o reenvio (que sobrescreve) para não contar duas conversões —
   * `desconhecido` quando não deu para saber se já havia um.
   */
  | {
      event: 'gabarito_enviado';
      respondidas: number;
      tipo_envio: 'primeiro' | 'reenvio' | 'desconhecido';
    }
  /** Um envio falhou no servidor. */
  | { event: 'envio_erro'; tipo: 'ensaio' | 'gabarito' }
  // ── Conta ──────────────────────────────────────────────────────────────
  /** O diálogo de conta abriu, e em qual passo. */
  | {
      event: 'auth_aberto';
      origem: AuthOrigem;
      passo:
        | 'ask'
        | 'sign-in'
        | 'sign-up'
        | 'forgot-password'
        /** Conta da Primum sem perfil nesta plataforma: o cadastro curto. */
        | 'complete-profile';
    }
  /** A resposta ao "Você já tem conta?" do gabarito. */
  | { event: 'ja_tem_conta'; resposta: 'sim' | 'nao' }
  /** GA4 recomendado: conta criada. */
  | { event: 'sign_up'; method: 'email'; origem: AuthOrigem }
  /**
   * GA4 recomendado: **um lead novo** — sai junto com o `sign_up`, porque o
   * cadastro é o momento em que o contato passa a existir. Login não é lead.
   */
  | { event: 'generate_lead'; lead_source: AuthOrigem }
  /** Cadastro com e-mail que já tinha conta: desviado para o login. */
  | { event: 'cadastro_email_existente'; origem: AuthOrigem }
  /** GA4 recomendado: entrou numa conta existente. */
  | { event: 'login'; method: 'email'; origem: AuthOrigem }
  /** Login recusado (credencial errada). Atrito, não pessoa. */
  | { event: 'login_erro'; origem: AuthOrigem }
  | { event: 'senha_redefinicao_solicitada' }
  | { event: 'logout' }
  // ── Painel ─────────────────────────────────────────────────────────────
  /** Uma aba do painel foi vista (ao abrir e a cada troca). */
  | {
      event: 'painel_aba_vista';
      aba: 'ensaio' | 'gabarito' | 'ranking' | 'prova-oficial' | 'diagnostico';
      sub_aba?: 'questoes' | 'diagnostico';
    }
  /** Um diagnóstico apareceu na tela, com a categoria da leitura. */
  | {
      event: 'diagnostico_visualizado';
      fonte: 'ensaio' | 'prova';
      categoria: string;
    }
  /** GA4 recomendado: o convite saiu (`item_id` = para onde ele leva). */
  | {
      event: 'share';
      method: 'nativo' | 'copiar_link' | 'whatsapp';
      content_type: 'convite';
      item_id: 'ensaio' | 'gabarito';
    };

/** Vai em **todo** evento: qual prova e qual edição (o contêiner é por prova). */
interface AnalyticsContext {
  prova: string;
  edicao: number;
}

let context: AnalyticsContext | null = null;

/** Chamado uma vez pelo `App`, com a prova e a edição do build. */
export function configureAnalytics(next: AnalyticsContext): void {
  context = next;
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Empurra o evento para o `dataLayer`, com a prova e a edição junto. **Nunca
 * lança**: medir não pode atrapalhar a ação medida. Sem o GTM (dev,
 * bloqueador), o evento só não vai a lugar nenhum.
 */
export function track(payload: AnalyticsEvent): void {
  try {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ ...context, ...payload });
  } catch {
    // Sem `window` (teste sem DOM) não há para onde mandar.
  }
}

const MILESTONES = [25, 50, 75, 100] as const;

/**
 * Os marcos de avanço (25, 50, 75, 100%) cruzados entre duas contagens. Uma
 * confirmação cruza no máximo um; um rascunho restaurado pode cruzar vários de
 * uma vez, e cada um sai uma vez só. Desmarcar não cruza nada para trás.
 */
export function crossedMilestones(
  before: number,
  after: number,
  total: number,
): (typeof MILESTONES)[number][] {
  if (total <= 0 || after <= before) return [];
  return MILESTONES.filter(
    (milestone) =>
      (before / total) * 100 < milestone && (after / total) * 100 >= milestone,
  );
}
